import { useEffect, useState } from "react";
import { Radio, ChevronRight, Loader2, SendHorizonal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MicButton from "@/components/ui/MicButton";
import { askLive, warmupLive } from "@/lib/memo";
import { cn } from "@/lib/utils";

export default function LiveAssistant({ dossierId }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!open || ready) return;
        warmupLive(dossierId).then(() => setReady(true)).catch(() => {});
    }, [open, ready, dossierId]);

    async function ask(text) {
        const question = (text || q).trim();
        if (!question || busy) return;
        setBusy(true); setQ("");
        setItems((l) => [{ id: crypto.randomUUID(), question, answer: null }, ...l]);
        try {
            const r = await askLive(dossierId, question);
            setItems((l) => l.map((it, i) => (i === 0 ? { ...it, answer: r.answer } : it)));
        } catch (e) {
            setItems((l) => l.map((it, i) => (i === 0 ? { ...it, answer: `⚠️ ${e.message}` } : it)));
        } finally { setBusy(false); }
    }

    return (
        // LOT 46 : conteneur de clipping (taille viewport) — empêche le panneau off-canvas (translate-x-full) d'élargir la page
        <div className="fixed inset-0 z-40 overflow-hidden pointer-events-none">
            <button type="button" data-testid="live-toggle" onClick={() => setOpen((o) => !o)} title="Assistant Live"
                className={cn("pointer-events-auto absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2 rounded-l-full px-3 py-2.5 text-xs font-semibold uppercase tracking-wider shadow-lg transition-transform", open ? "translate-x-full" : "bg-primary text-primary-foreground hover:pr-4")}>
                <Radio className="h-4 w-4 animate-pulse" /> Live
            </button>
            <aside data-testid="live-panel" aria-hidden={!open} style={{ maxHeight: "80vh", overflowY: "auto" }} className={cn("pointer-events-auto absolute right-0 top-[10vh] w-full sm:w-[380px] bg-card border-l border-y border-border rounded-l-lg shadow-2xl flex flex-col transition-transform duration-300", open ? "translate-x-0" : "translate-x-full")}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                    <Radio className="h-4 w-4 text-rose-600 animate-pulse" />
                    <h3 className="font-display text-lg text-foreground">Assistant Live</h3>
                    <span data-testid="live-status" className="text-[10px] uppercase tracking-wider text-muted-foreground">{ready ? "prêt · 1 phrase" : "chargement du dossier…"}</span>
                    <button type="button" data-testid="live-close" onClick={() => setOpen(false)} className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent"><ChevronRight className="h-4 w-4" /></button>
                </div>
                <div data-testid="live-answers" className="flex-1 min-h-[160px] max-h-[50vh] overflow-y-auto px-4 py-3 space-y-3">
                    {items.length === 0 && <p className="text-xs text-muted-foreground">Posez une question chiffrée : « Passif exigible ? », « CA 2024 ? », « Trésorerie fin M+3 scénario RJ ? »</p>}
                    {items.map((it) => (
                        <div key={it.id} data-testid="live-item" className="rounded-md border border-border bg-background px-3 py-2">
                            <p className="text-xs text-muted-foreground">{it.question}</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">{it.answer == null ? <Loader2 className="h-4 w-4 animate-spin inline" /> : it.answer}</p>
                        </div>
                    ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2 border-t border-border px-3 py-3">
                    <Input data-testid="live-input" value={q} onChange={(e) => setQ(e.target.value)} disabled={busy} placeholder="Question rapide…" className="h-10 bg-background" />
                    <MicButton testId="live-mic" disabled={busy} onText={(t) => ask(t)} />
                    <Button type="submit" data-testid="live-send" disabled={busy || !q.trim()} className="h-10 w-10 p-0 rounded-full bg-primary hover:bg-primary/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}</Button>
                </form>
            </aside>
        </div>
    );
}
